// Decrypt encrypted PDFs entirely in the browser.
//
// Why this exists: pdf.js renders encrypted PDFs (empty-user-password files are
// common — banks, gov forms, exports all set an /Encrypt dict just to flag
// restrictions), so such files *open and preview* fine. But pdf-lib, which
// powers export, cannot decrypt content streams. Loading with
// `ignoreEncryption` only skips the check — the copied pages stay encrypted and
// the saved PDF renders blank. So a user could open a file, reorder/rotate, hit
// Download, and get a blank document.
//
// qpdf (compiled to WASM) does real decryption for every scheme pdf.js can open,
// including AES-256 (V5/R6). We run it once at intake so the rest of the app —
// viewer and exporter alike — works on plain, unencrypted bytes. The ~1.3 MB
// wasm module is imported dynamically, so it only loads when an encrypted file
// is actually opened; normal PDFs never pay for it. Everything stays on-device.

// Vite resolves this to a served, hashed URL for the wasm binary.
import qpdfWasmUrl from "@jspawn/qpdf-wasm/qpdf.wasm?url";

/**
 * Cheap check for an encryption dictionary. Scans for the `/Encrypt` key, which
 * only appears in the trailer of an encrypted file. Avoids spinning up the wasm
 * module for the common (unencrypted) case.
 */
export function isEncryptedPdf(bytes: Uint8Array): boolean {
  // /Encrypt = 0x2F 45 6E 63 72 79 70 74. Scan the raw bytes; the key lives in
  // the trailer/xref area and there is no risk of a false positive inside a
  // stream because encrypted streams are the whole point here.
  const needle = [0x2f, 0x45, 0x6e, 0x63, 0x72, 0x79, 0x70, 0x74]; // "/Encrypt"
  const limit = bytes.length - needle.length;
  for (let i = 0; i <= limit; i++) {
    let match = true;
    for (let j = 0; j < needle.length; j++) {
      if (bytes[i + j] !== needle[j]) {
        match = false;
        break;
      }
    }
    if (match) return true;
  }
  return false;
}

export class PdfDecryptError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PdfDecryptError";
  }
}

type QpdfModule = {
  FS: {
    writeFile: (path: string, data: Uint8Array) => void;
    readFile: (path: string) => Uint8Array;
    unlink: (path: string) => void;
  };
  callMain: (args: string[]) => number;
};

let modulePromise: Promise<QpdfModule> | null = null;

// Instantiate qpdf once and reuse it. The Emscripten runtime is stateful, but
// each call uses fresh, uniquely named virtual files, so reuse is safe.
async function loadQpdf(): Promise<QpdfModule> {
  if (!modulePromise) {
    modulePromise = import("@jspawn/qpdf-wasm/qpdf.mjs").then((mod) =>
      (mod.default as (config: unknown) => Promise<QpdfModule>)({
        noInitialRun: true,
        locateFile: () => qpdfWasmUrl,
        // Swallow qpdf's stdout/stderr; we report failures via exit code.
        print: () => {},
        printErr: () => {},
      }),
    );
  }
  return modulePromise;
}

/**
 * Return a decrypted copy of `bytes`. Assumes the file is encrypted with an
 * empty user password (the case pdf.js can open without prompting). Throws
 * `PdfDecryptError` if qpdf cannot remove the encryption (e.g. the file needs a
 * real open password).
 */
export async function decryptPdf(bytes: Uint8Array): Promise<Uint8Array> {
  const qpdf = await loadQpdf();
  const stamp = Date.now().toString(36) + Math.random().toString(36).slice(2);
  const inPath = `/in_${stamp}.pdf`;
  const outPath = `/out_${stamp}.pdf`;

  qpdf.FS.writeFile(inPath, bytes);
  try {
    const rc = qpdf.callMain(["--decrypt", "--password=", inPath, outPath]);
    if (rc !== 0) {
      throw new PdfDecryptError(
        "This PDF is password-protected and can't be opened without its password.",
      );
    }
    const out = qpdf.FS.readFile(outPath);
    // Copy off the wasm heap before we unlink, so the returned array is stable.
    return out.slice();
  } finally {
    safeUnlink(qpdf, inPath);
    safeUnlink(qpdf, outPath);
  }
}

function safeUnlink(qpdf: QpdfModule, path: string): void {
  try {
    qpdf.FS.unlink(path);
  } catch {
    // File may not exist (e.g. decrypt failed before writing output).
  }
}
